import { useEffect, useRef, useState } from 'react';

import { Button } from '../button';
import { Spinner } from '../spinner';
import { IMAGE_MAX_AUTO_RETRIES, IMAGE_RETRY_DELAYS_MS, withRetryParam } from './image-retry';

export { IMAGE_MAX_AUTO_RETRIES, IMAGE_RETRY_DELAYS_MS, withRetryParam } from './image-retry';

import { ImageZoomModal } from './image-zoom-modal';

export { ImageZoomModal };

const ImageLoadManager = (() => {
  let subscribers: (() => void)[] = [];
  let visibleImageCount = 0;
  let visibleLoaded = 0;
  let allVisibleLoaded = false;

  return {
    registerVisible: () => {
      visibleImageCount += 1;
    },
    notifyVisibleLoaded: () => {
      visibleLoaded += 1;
      if (visibleLoaded >= visibleImageCount && !allVisibleLoaded) {
        allVisibleLoaded = true;
        subscribers.forEach((cb) => cb());
      }
    },
    subscribe: (cb: () => void) => {
      subscribers.push(cb);
    },
    unsubscribe: (cb: () => void) => {
      subscribers = subscribers.filter((fn) => fn !== cb);
    },
    isReady: () => allVisibleLoaded,
  };
})();

interface ILazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  allowZoom?: boolean;
  /** Серая пульсирующая заглушка вместо спиннера, пока картинка грузится */
  skeleton?: boolean;
}

export const LazyImage = ({ src, alt, allowZoom, skeleton = false, ...rest }: ILazyImageProps) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!failed || attempt >= IMAGE_MAX_AUTO_RETRIES) return;

    const timer = setTimeout(() => {
      setFailed(false);
      setAttempt((prev) => prev + 1);
    }, IMAGE_RETRY_DELAYS_MS[attempt] ?? 1500);

    return () => clearTimeout(timer);
  }, [failed, attempt]);

  const gaveUp = failed && attempt >= IMAGE_MAX_AUTO_RETRIES;

  useEffect(() => {
    if (!imgRef.current) return;

    const node = imgRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            setHasBeenVisible(true);
            ImageLoadManager.registerVisible();
          }
        });
      },
      { rootMargin: '100px' },
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
    };
  }, []);

  useEffect(() => {
    if (hasBeenVisible || ImageLoadManager.isReady()) {
      setShouldLoad(true);

      return;
    }

    const callback = () => setShouldLoad(true);
    ImageLoadManager.subscribe(callback);

    return () => {
      ImageLoadManager.unsubscribe(callback);
    };
  }, [hasBeenVisible]);

  return (
    <div {...rest} className={`${rest.className ?? ''} relative flex items-center justify-center overflow-hidden`}>
      {!loaded && !gaveUp && skeleton && <div className="absolute inset-0 animate-pulse bg-white/10" aria-hidden />}
      {!loaded && !gaveUp && !skeleton && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner className="text-ink-subtle" />
        </div>
      )}
      {gaveUp && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 px-2 text-center text-ui-xs text-white/80">
          <span>Не удалось загрузить</span>
          <Button
            size="sm"
            variant="ghostInverse"
            onClick={(e) => {
              e.stopPropagation();
              setFailed(false);
              setAttempt((prev) => prev + 1);
            }}
          >
            Повторить
          </Button>
        </div>
      )}
      <img
        ref={imgRef}
        src={shouldLoad ? withRetryParam(src, attempt) : undefined}
        alt={alt}
        loading="lazy"
        onError={() => setFailed(true)}
        onLoad={() => {
          setLoaded(true);
          setFailed(false);
          if (inView) {
            ImageLoadManager.notifyVisibleLoaded();
          }
        }}
        className={'absolute top-0 right-0 bottom-0 left-0 transition-opacity duration-300 w-full h-full'}
      />
      {allowZoom && loaded && (
        <button
          onClick={() => setZoomed(true)}
          className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(0,0,0,0.25)] shadow-overlay cursor-pointer transition-colors hover:bg-[rgba(0,0,0,0.65)]"
        >
          🔍
        </button>
      )}
      {zoomed && <ImageZoomModal src={src} onClose={() => setZoomed(false)} />}
    </div>
  );
};
